import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, homeFor, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AdminLayout from "@/layouts/AdminLayout";
import WorkOrders from "@/pages/admin/WorkOrders";
import WorkOrderForm from "@/pages/admin/WorkOrderForm";
import WorkOrderDetail from "@/pages/admin/WorkOrderDetail";
import Clients from "@/pages/admin/Clients";
import ClientDetail from "@/pages/admin/ClientDetail";
import EquipmentCatalog from "@/pages/admin/EquipmentCatalog";
import Inventory from "@/pages/admin/Inventory";
import Users from "@/pages/admin/Users";
import Settings from "@/pages/admin/Settings";
import Maintenance from "@/pages/admin/Maintenance";
import Finance from "@/pages/admin/Finance";
import EquipmentHistory from "@/pages/admin/EquipmentHistory";
import CommercialCatalog from "@/pages/admin/CommercialCatalog";
import TechHome from "@/pages/tech/TechHome";
import TechWorkOrder from "@/pages/tech/TechWorkOrder";
import TechNewWorkOrder from "@/pages/tech/TechNewWorkOrder";
import TechInventory from "@/pages/tech/TechInventory";
import SignPage from "@/pages/SignPage";
import { SurveyDetail, SurveyList } from "@/pages/Surveys";

const Splash = () => (
  <div className="flex min-h-screen items-center justify-center" data-testid="app-loading">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

const Protected = ({ roles, children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
};

const Home = () => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  return <Navigate to={user ? homeFor(user) : "/login"} replace />;
};

const OFFICE = ["admin", "supervisor", "finance"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/firma/:token" element={<SignPage />} />
          <Route path="/tecnico" element={<Protected roles={["technician"]}><TechHome /></Protected>} />
          <Route path="/tecnico/os/nueva" element={<Protected roles={["technician"]}><TechNewWorkOrder /></Protected>} />
          <Route path="/tecnico/inventario" element={<Protected roles={["technician"]}><TechInventory /></Protected>} />
          <Route path="/tecnico/os/:id" element={<Protected roles={["technician"]}><TechWorkOrder /></Protected>} />
          <Route path="/tecnico/levantamientos" element={<Protected roles={["technician"]}><SurveyList tech /></Protected>} />
          <Route path="/tecnico/levantamientos/:id" element={<Protected roles={["technician"]}><SurveyDetail tech /></Protected>} />
          <Route path="/admin" element={<Protected roles={OFFICE}><AdminLayout /></Protected>}>
            <Route index element={<WorkOrders />} />
            <Route path="os/nueva" element={<WorkOrderForm />} />
            <Route path="os/:id" element={<WorkOrderDetail />} />
            <Route path="os/:id/editar" element={<WorkOrderForm />} />
            <Route path="levantamientos" element={<SurveyList />} />
            <Route path="mantenimientos" element={<Maintenance />} />
            <Route path="finanzas" element={<Finance />} />
            <Route path="historial-equipos" element={<EquipmentHistory />} />
            <Route path="catalogo-comercial" element={<CommercialCatalog />} />
            <Route path="levantamientos/:id" element={<SurveyDetail />} />
            <Route path="clientes" element={<Clients />} />
            <Route path="clientes/:id" element={<ClientDetail />} />
            <Route path="catalogo" element={<EquipmentCatalog />} />
            <Route path="inventario" element={<Inventory />} />
            <Route path="usuarios" element={<Users />} />
            <Route path="configuracion" element={<Settings />} />
          </Route>
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
